import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { API } from "@/App";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import { 
  PiggyBank, Wallet, Target, Sparkles, TrendingUp, 
  Plus, ChevronRight, Plane, MapPin, Calendar,
  Flame, Award, Share2, Navigation
} from "lucide-react";
import { useNearbyAirport } from "@/hooks/useNearbyAirport";

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [goals, setGoals] = useState([]);
  const [streakData, setStreakData] = useState(null);
  const [badges, setBadges] = useState([]);
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [challengeAmount, setChallengeAmount] = useState("");
  const [challengeGoalId, setChallengeGoalId] = useState("");
  const [isCompletingChallenge, setIsCompletingChallenge] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [userRes, goalsRes, streakRes, badgesRes, challengeRes] = await Promise.all([
        fetch(`${API}/auth/me`, { credentials: "include" }),
        fetch(`${API}/goals/`, { credentials: "include" }),
        fetch(`${API}/gamification/streaks`, { credentials: "include" }),
        fetch(`${API}/gamification/badges`, { credentials: "include" }),
        fetch(`${API}/gamification/daily-challenge`, { credentials: "include" })
      ]);
      
      if (userRes.ok) {
        const userData = await userRes.json();
        setUser(userData);
        // Redirect to onboarding if not completed
        if (!userData.onboarding_completed) {
          navigate("/onboarding", { state: { user: userData } });
          return;
        }
      }
      if (goalsRes.ok) setGoals(await goalsRes.json());
      if (streakRes.ok) setStreakData(await streakRes.json());
      if (challengeRes.ok) {
        const ch = await challengeRes.json();
        setDailyChallenge(ch);
        setChallengeAmount(ch.suggested_amount?.toString() || "5");
      }
      if (badgesRes.ok) {
        const data = await badgesRes.json();
        setBadges(data.badges || []);
        if (data.newly_earned?.length > 0) {
          data.newly_earned.forEach(id => {
            const badge = data.badges.find(b => b.badge_id === id);
            if (badge) toast.success(`Badge earned: ${badge.name}!`);
          });
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard. Please refresh.");
    } finally {
      setIsLoading(false);
    }
  };

  const completeChallenge = async () => {
    const amount = parseFloat(challengeAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setIsCompletingChallenge(true);
    try {
      const res = await fetch(`${API}/gamification/daily-challenge/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount, goal_id: challengeGoalId || null })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed");
      }
      toast.success(`Challenge completed! You saved $${amount.toFixed(2)}`);
      setDailyChallenge(prev => ({ ...prev, completed: true, completed_amount: amount }));
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsCompletingChallenge(false);
    }
  };
  
  const totalSaved = goals.reduce((sum, g) => sum + g.current_amount, 0);
  const totalTarget = goals.reduce((sum, g) => sum + g.target_amount, 0);
  const activeGoals = goals.filter(g => g.status === "active");
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="dashboard-skeleton">
        {/* Skeleton Header */}
        <div className="bg-gradient-to-br from-sky-500 to-sky-600 px-6 pt-8 pb-12 rounded-b-3xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="h-4 w-16 bg-white/20 rounded mb-2 animate-pulse" />
              <div className="h-7 w-32 bg-white/30 rounded animate-pulse" />
            </div>
            <div className="w-10 h-10 rounded-full bg-white/20 animate-pulse" />
          </div>
          <div className="bg-white/10 rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 animate-pulse" />
              <div>
                <div className="h-3 w-16 bg-white/20 rounded mb-2 animate-pulse" />
                <div className="h-8 w-28 bg-white/30 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
        {/* Skeleton Content */}
        <div className="px-6 -mt-4">
          <div className="grid grid-cols-3 gap-3 mb-8">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="h-5 w-5 bg-slate-200 rounded mx-auto mb-2 animate-pulse" />
                <div className="h-6 w-10 bg-slate-200 rounded mx-auto mb-1 animate-pulse" />
                <div className="h-3 w-14 bg-slate-100 rounded mx-auto animate-pulse" />
              </div>
            ))}
          </div>
          {[1,2].map(i => (
            <div key={i} className="bg-white rounded-2xl p-5 mb-4 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-slate-200 animate-pulse" />
                <div className="flex-1">
                  <div className="h-5 w-3/4 bg-slate-200 rounded mb-2 animate-pulse" />
                  <div className="h-3 w-1/2 bg-slate-100 rounded mb-3 animate-pulse" />
                  <div className="h-2 w-full bg-slate-100 rounded animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <BottomNav />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-gradient-to-br from-sky-500 to-sky-600 text-white px-6 pt-8 pb-12 rounded-b-3xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sky-100">Hello,</p>
            <h1 className="text-2xl font-bold">{user?.name?.split(' ')[0] || "Traveler"} 👋</h1>
          </div>
          <button 
            onClick={() => navigate("/settings")}
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center"
            data-testid="settings-btn"
          >
            <img 
              src={user?.picture || "https://via.placeholder.com/40"} 
              alt="Profile" 
              className="w-8 h-8 rounded-full object-cover"
            />
          </button>
        </div>
        
        {/* Savings Card */}
        <div 
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-5 cursor-pointer hover:bg-white/15 transition-colors"
          onClick={() => navigate("/wallet")}
          data-testid="wallet-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <PiggyBank className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-sky-100">Total Saved</p>
                <p className="text-3xl font-bold font-accent">${(user?.total_saved || 0).toFixed(2)}</p>
              </div>
            </div>
            <Button 
              size="sm" 
              className="bg-white text-sky-600 hover:bg-sky-50 rounded-full font-bold"
              onClick={(e) => { e.stopPropagation(); navigate("/wallet"); }}
              data-testid="add-funds-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Log
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="px-6 -mt-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="stats-card">
            <Target className="w-5 h-5 text-sky-500 mx-auto mb-1" />
            <p className="stats-value text-2xl">{activeGoals.length}</p>
            <p className="stats-label text-xs">Active Goals</p>
          </div>
          <div className="stats-card">
            <TrendingUp className="w-5 h-5 text-amber-500 mx-auto mb-1" />
            <p className="stats-value text-2xl">${totalSaved.toFixed(0)}</p>
            <p className="stats-label text-xs">Total Saved</p>
          </div>
          <div
            className="stats-card relative group cursor-default"
            style={{ background: streakData?.current_streak > 0 ? "linear-gradient(135deg, #FFF7ED, #FFEDD5)" : undefined }}
            title="Log savings every day to build your streak. Earn reward credits at 7, 14, and 30-day milestones!"
          >
            <Flame className={`w-5 h-5 mx-auto mb-1 ${streakData?.current_streak > 0 ? "text-orange-500 streak-flame" : "text-slate-400"}`} />
            <p className={`text-2xl font-bold ${streakData?.current_streak > 0 ? "text-orange-500" : "text-slate-400"}`}>{streakData?.current_streak || 0}</p>
            <p className="stats-label text-xs">Day Streak</p>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-slate-800 text-white text-xs rounded-xl px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-center shadow-lg">
              Log savings daily to keep your streak. Earn credits at 7, 14 & 30 days!
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
            </div>
          </div>
        </div>
        
        {/* Badges Section */}
        {badges.filter(b => b.earned).length > 0 && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" /> Badges
              </h2>
              <span className="text-sm text-slate-500">{badges.filter(b => b.earned).length}/{badges.length}</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {badges.filter(b => b.earned).map(badge => (
                <div 
                  key={badge.badge_id}
                  className="flex-shrink-0 w-20 text-center badge-earned"
                  data-testid={`badge-${badge.badge_id}`}
                >
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center mb-1 border-2 border-amber-300">
                    <BadgeIcon name={badge.icon} className="w-7 h-7 text-amber-600" />
                  </div>
                  <p className="text-xs font-medium text-slate-700 truncate">{badge.name}</p>
                </div>
              ))}
            </div>
          </section>
        )}
        
        {/* Daily Challenge */}
        {dailyChallenge && (
          <section className="mb-8" data-testid="daily-challenge-section">
            <div className="rounded-2xl overflow-hidden border-2 border-amber-200" style={{
              background: dailyChallenge.completed 
                ? "linear-gradient(135deg, #ECFDF5, #D1FAE5)" 
                : "linear-gradient(135deg, #FFFBEB, #FEF3C7)"
            }}>
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className={`w-5 h-5 ${dailyChallenge.completed ? "text-emerald-500" : "text-amber-500"}`} />
                    <span className="text-xs font-bold uppercase tracking-wider text-amber-700">Daily Challenge</span>
                  </div>
                  <span className="text-xs text-slate-500">{dailyChallenge.weekly_completions}/7 this week</span>
                </div>
                <h3 className="font-bold text-slate-900 text-lg mb-1">{dailyChallenge.title}</h3>
                <p className="text-sm text-slate-600 mb-4">{dailyChallenge.description}</p>
                
                {dailyChallenge.completed ? (
                  <div className="flex items-center gap-2 text-emerald-600" data-testid="challenge-completed">
                    <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center">
                      <Flame className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="font-bold">Completed! Saved ${dailyChallenge.completed_amount?.toFixed(2)}</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                        <input
                          type="number"
                          value={challengeAmount}
                          onChange={e => setChallengeAmount(e.target.value)}
                          className="w-full pl-7 pr-3 py-2 rounded-xl border border-amber-300 bg-white text-sm font-medium"
                          placeholder={dailyChallenge.suggested_amount?.toString()}
                          data-testid="challenge-amount-input"
                        />
                      </div>
                      {activeGoals.length > 0 && (
                        <select
                          value={challengeGoalId}
                          onChange={e => setChallengeGoalId(e.target.value)}
                          className="px-3 py-2 rounded-xl border border-amber-300 bg-white text-sm max-w-[140px]"
                          data-testid="challenge-goal-select"
                        >
                          <option value="">No goal</option>
                          {activeGoals.map(g => (
                            <option key={g.goal_id} value={g.goal_id}>{g.title}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <Button
                      onClick={completeChallenge}
                      disabled={isCompletingChallenge}
                      className="w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold h-11"
                      data-testid="complete-challenge-btn"
                    >
                      {isCompletingChallenge ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>I Did It!</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
        
        {/* Goals Section */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900">Your Goals</h2>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate("/goals")}
              className="text-sky-600 font-semibold"
            >
              See All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          {activeGoals.length === 0 ? (
            <div className="savecation-card text-center py-8">
              <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-4">
                <PiggyBank className="w-8 h-8 text-sky-500" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">No goals yet!</h3>
              <p className="text-slate-500 mb-4">Start saving for your dream vacation</p>
              <Button 
                onClick={() => navigate("/goals")}
                className="savecation-button"
                data-testid="create-first-goal-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Create Your First Goal
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {activeGoals.slice(0, 3).map(goal => {
                const current = goal.current_amount ?? 0;
                const target = goal.target_amount ?? 0;
                const progress = target > 0 ? Math.min((current / target) * 100, 100) : 0;
                return (
                  <div
                    key={goal.goal_id}
                    className="savecation-card cursor-pointer"
                    onClick={() => navigate("/goals")}
                    data-testid={`goal-card-${goal.goal_id}`}
                  >
                    <div className="flex items-start gap-4">
                      {goal.image_url ? (
                        <img src={goal.image_url} alt={goal.title} className="w-16 h-16 rounded-2xl object-cover" />
                      ) : (
                        <div className="w-16 h-16 rounded-2xl bg-sky-100 flex items-center justify-center">
                          <MapPin className="w-8 h-8 text-sky-500" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-bold text-slate-900">{goal.title}</h3>
                          <span className="text-sm font-medium text-sky-600">{Math.round(progress)}%</span>
                        </div>
                        {goal.destination && (
                          <p className="text-sm text-slate-500 flex items-center gap-1 mb-2">
                            <Plane className="w-3 h-3" /> {goal.destination}
                          </p>
                        )}
                        <Progress value={progress} className="h-2 rounded-full" />
                        <p className="text-sm text-slate-500 mt-2">
                          ${current.toFixed(0)} of ${target.toFixed(0)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
        
        {/* AI Suggestion Card */}
        <section className="mb-8">
          <div 
            className="savecation-card bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200 cursor-pointer"
            onClick={() => navigate("/planner")}
            data-testid="ai-suggestion-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-amber-200 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-900 mb-1">AI Travel Coach</h3>
                <p className="text-sm text-slate-600">Get personalized trip suggestions and advice</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        </section>
        
        {/* Deals Near You */}
        <NearbyDealsSection user={user} navigate={navigate} />
        
        {/* Quick Actions */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-4">
            <Button 
              variant="outline"
              className="h-auto py-4 flex-col gap-2 rounded-2xl border-2 border-slate-100 hover:border-sky-200"
              onClick={() => navigate("/deals")}
              data-testid="browse-deals-btn"
            >
              <Plane className="w-6 h-6 text-sky-500" />
              <span className="font-semibold text-slate-700">Browse Deals</span>
            </Button>
            <Button 
              variant="outline"
              className="h-auto py-4 flex-col gap-2 rounded-2xl border-2 border-slate-100 hover:border-sky-200"
              onClick={() => navigate("/planner")}
              data-testid="plan-trip-btn"
            >
              <Calendar className="w-6 h-6 text-amber-500" />
              <span className="font-semibold text-slate-700">Plan a Trip</span>
            </Button>
          </div>
        </section>
        
        {/* Pro Upgrade Banner (for free users) */}
        {user?.subscription_tier === "free" && (
          <section className="mb-24">
            <div 
              className="rounded-3xl p-6 text-center cursor-pointer"
              style={{ background: "linear-gradient(135deg, #FBBF24, #F59E0B)" }}
              onClick={() => navigate("/settings")}
              data-testid="upgrade-banner"
            >
              <p className="text-amber-900 font-bold text-lg mb-2">Unlock Unlimited Goals</p>
              <p className="text-amber-800 text-sm mb-4">Upgrade to Pro for group goals, more AI requests, and more!</p>
              <Button className="bg-white text-amber-700 hover:bg-amber-50 rounded-full font-bold">
                Upgrade to Pro - $9.99/mo
              </Button>
            </div>
          </section>
        )}
      </main>
      
      <BottomNav />
    </div>
  );
}

function BadgeIcon({ name, className }) {
  const icons = {
    "piggy-bank": PiggyBank,
    "flame": Flame,
    "zap": Sparkles,
    "trophy": Award,
    "target": Target,
    "award": Award,
    "trending-up": TrendingUp,
    "star": Sparkles,
    "users": MapPin,
    "gift": Wallet,
    "plane": Plane,
    "sparkles": Sparkles
  };
  const Icon = icons[name] || Award;
  return <Icon className={className} />;
}

const NEARBY_CACHE_KEY = "savecation_nearby_deals";
const NEARBY_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

function NearbyDealsSection({ user, navigate }) {
  const { airport, deals: freshDeals, loading } = useNearbyAirport(user);

  // Cache deals for 24h to avoid re-fetching on every visit
  const [deals, setDeals] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(NEARBY_CACHE_KEY) || "null");
      if (cached && Date.now() - cached.ts < NEARBY_CACHE_TTL) return cached.deals;
    } catch {}
    return [];
  });

  useEffect(() => {
    if (!loading && freshDeals.length > 0) {
      setDeals(freshDeals);
      try {
        localStorage.setItem(NEARBY_CACHE_KEY, JSON.stringify({ deals: freshDeals, ts: Date.now() }));
      } catch {}
    }
  }, [loading, freshDeals]);

  if (loading) {
    return (
      <section className="mb-8">
        <div className="h-5 w-40 bg-slate-200 rounded mb-3 animate-pulse" />
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[1,2,3].map(i => (
            <div key={i} className="flex-shrink-0 w-44 h-48 bg-slate-200 rounded-2xl animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (!deals.length) return null;

  return (
    <section className="mb-8" data-testid="nearby-deals-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Navigation className="w-4 h-4 text-sky-500" /> Deals from {airport?.city || airport?.code}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/deals")}
          className="text-sky-600 font-semibold text-xs"
        >
          See All <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {deals.slice(0, 4).map(deal => (
          <div
            key={deal.deal_id}
            className="flex-shrink-0 w-44 rounded-2xl overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition-shadow snap-start"
            onClick={() => navigate("/deals")}
            data-testid={`nearby-deal-${deal.destination}`}
          >
            <div className="relative h-28">
              <img src={deal.image} alt={deal.city || deal.destination} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-2 left-2 right-2">
                <p className="text-white font-bold text-sm">{deal.city || deal.destination}</p>
              </div>
            </div>
            <div className="p-3 bg-white">
              <p className="text-emerald-600 font-bold text-lg">${deal.price}</p>
              <p className="text-slate-400 text-xs">round trip</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
